import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/infra/supabase/client";

interface FirstStep {
  key: string;
  label: string;
  completed: boolean;
  href?: string;
}

interface UseFirstStepsReturn {
  steps: FirstStep[];
  completedCount: number;
  totalCount: number;
  allCompleted: boolean;
  dismissed: boolean;
  dismiss: () => void;
  isLoading: boolean;
}

const FIRST_STEPS_DISMISSED_KEY = "engdosim_first_steps_dismissed";

export function useFirstSteps(): UseFirstStepsReturn {
  const [steps, setSteps] = useState<FirstStep[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(FIRST_STEPS_DISMISSED_KEY) === "true",
  );

  const fetchSteps = useCallback(async () => {
    try {
      setIsLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      // Check real app state instead of milestones table
      const [
        { data: profile },
        { data: userMentor },
        { data: templeProgress },
        { data: abilityExec },
        { data: taskProgress },
      ] = await Promise.all([
        supabase
          .from("profiles")
          .select("onboarding_completed")
          .eq("id", user.id)
          .single(),
        supabase
          .from("user_mentors")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle(),
        supabase
          .from("temple_progress")
          .select("id")
          .eq("user_id", user.id)
          .neq("status", "locked")
          .limit(1)
          .maybeSingle(),
        supabase
          .from("ability_executions")
          .select("id")
          .eq("user_id", user.id)
          .limit(1)
          .maybeSingle(),
        supabase
          .from("task_progress")
          .select("id")
          .eq("user_id", user.id)
          .eq("status", "completed")
          .limit(1)
          .maybeSingle(),
      ]);

      const stepDefs: FirstStep[] = [
        {
          key: "onboarding",
          label: "Completar onboarding",
          completed: profile?.onboarding_completed === true,
        },
        {
          key: "mentor",
          label: "Escolher seu mentor",
          completed: !!userMentor,
          href: "/mentor",
        },
        {
          key: "first_temple_started",
          label: "Explorar o Templo 1",
          completed: !!templeProgress,
          href: "/jornada",
        },
        {
          key: "first_mission_started",
          label: "Iniciar primeira missao",
          completed: !!abilityExec,
          href: "/jornada",
        },
        {
          key: "first_task_completed",
          label: "Completar uma tarefa",
          completed: !!taskProgress,
          href: "/jornada",
        },
      ];

      setSteps(stepDefs);
    } catch (error) {
      console.error("Error fetching first steps:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSteps();
  }, [fetchSteps]);

  const completedCount = steps.filter((s) => s.completed).length;

  const dismiss = useCallback(() => {
    localStorage.setItem(FIRST_STEPS_DISMISSED_KEY, "true");
    setDismissed(true);
  }, []);

  return {
    steps,
    completedCount,
    totalCount: steps.length,
    allCompleted: steps.length > 0 && completedCount === steps.length,
    dismissed,
    dismiss,
    isLoading,
  };
}
